import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { StepIcon } from './StepIcon';
import { ScrapingReport } from './ScrapingReport';
import { stepClassName, voiceCloneLabelText } from '../../utils/training';
import type { StepStatus, TrainingProgressViewProps } from '../../types';

type IdleVideoStatus = TrainingProgressViewProps['idleVideoStatus'];

function getIdleVideoPresentation(
  status: IdleVideoStatus,
  trainingIsDone: boolean
): { label: string; stepStatus: StepStatus } {
  if (status === 'processing' && trainingIsDone) {
    return {
      label: 'Idle animation is continuing in the background',
      stepStatus: 'pending',
    };
  }
  if (status === 'processing') {
    return { label: 'Generating idle animation...', stepStatus: 'active' };
  }
  if (status === 'done') {
    return { label: 'Idle animation ready', stepStatus: 'done' };
  }
  return {
    label: 'Idle animation unavailable - generate it later from Echo Profile',
    stepStatus: 'error',
  };
}

/** Training progress UI with step indicators, voice clone status, and idle video status. */
export function TrainingProgressView({
  trainingSteps,
  trainingDone,
  trainingError,
  isFullyDone,
  voiceClone,
  preloadedConfig,
  scrapingQualityReport,
  retrainBenchmark,
  idleVideoStatus,
  onTrainingComplete,
  onRetry,
  onRetryVoiceClone,
  onBackFromProgress,
}: TrainingProgressViewProps) {
  const completedSteps = trainingSteps.filter(
    (step) => step.status === 'done' || step.status === 'skipped'
  ).length;
  const totalSteps = trainingSteps.length || 1;
  const progressPercent = isFullyDone ? 100 : Math.round((completedSteps / totalSteps) * 100);

  const hasAnyError = trainingError || voiceClone.hasError;
  const idleVideoPresentation = getIdleVideoPresentation(idleVideoStatus, isFullyDone);

  return (
    <div className='animate-slide-in w-full'>
      <h2 className='mb-2 font-proxima text-24 font-extrabold uppercase leading-47 text-brand-800 md:text-40'>
        TRAINING YOUR ECHO
      </h2>
      <p className='mb-8 font-sans text-16 font-medium leading-27 text-gray-530'>
        {isFullyDone
          ? 'Your echo has been created successfully.'
          : 'Please wait while we prepare your digital echo...'}
      </p>

      {/* Progress Bar */}
      <div className='mb-2 h-[4px] w-full overflow-hidden rounded-[2px] bg-gray-200'>
        <div
          className={clsx(
            'h-full bg-accent-300 transition-all duration-700 ease-out',
            !isFullyDone && !hasAnyError && 'animate-pulse'
          )}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <p className='mb-8 font-sans text-12 font-medium text-gray-490'>
        {hasAnyError
          ? 'An error occurred'
          : isFullyDone
            ? 'Processing complete'
            : `${progressPercent}% complete`}
      </p>

      {/* Status Card */}
      <div className={clsx('mb-8 flex w-full flex-col rounded-20 p-8', 'bg-grace-period-gradient')}>
        <h3 className='mb-6 font-sans text-24 font-bold leading-27 text-dark-100'>
          {isFullyDone ? 'Your digital echo is ready' : 'We are preparing your digital echo'}
        </h3>

        <div className='flex flex-col gap-4'>
          {trainingSteps.map((step, index) => (
            <div key={index} className='flex items-start gap-3'>
              <StepIcon status={step.status} />
              <span
                className={clsx(
                  'font-sans text-16 font-normal leading-24',
                  stepClassName(step.status)
                )}
              >
                {step.label}
              </span>
            </div>
          ))}

          {voiceClone.pendingFile && trainingDone && (
            <div className='flex items-start gap-3'>
              <StepIcon status={voiceClone.status} />
              <span
                className={clsx(
                  'font-sans text-16 font-normal leading-24',
                  stepClassName(voiceClone.status)
                )}
              >
                {voiceCloneLabelText(voiceClone.status)}
              </span>
            </div>
          )}

          {voiceClone.pendingFile && !trainingDone && !trainingError && (
            <div className='flex items-start gap-3'>
              <StepIcon status='pending' />
              <span className='font-sans text-16 font-normal leading-24 text-gray-350'>
                Uploading voice clone...
              </span>
            </div>
          )}

          {idleVideoStatus !== 'none' && (
            <div className='flex items-start gap-3'>
              <StepIcon status={idleVideoPresentation.stepStatus} />
              <span
                className={clsx(
                  'font-sans text-16 font-normal leading-24',
                  stepClassName(idleVideoPresentation.stepStatus)
                )}
              >
                {idleVideoPresentation.label}
              </span>
            </div>
          )}

          {trainingSteps.length === 0 && !trainingError && (
            <div className='flex items-start gap-3'>
              <StepIcon status='active' />
              <span className='font-sans text-16 font-normal leading-24 text-gray-400'>
                Connecting to server...
              </span>
            </div>
          )}
        </div>

        {scrapingQualityReport && trainingSteps[0]?.status === 'done' && (
          <ScrapingReport report={scrapingQualityReport} />
        )}

        {retrainBenchmark && (
          <div className='mt-6 rounded-10 border border-green-200 bg-green-50 p-4'>
            <p className='mb-3 font-sans text-14 font-bold text-dark-100'>Completeness benchmark</p>
            <div className='flex items-center justify-between gap-4 font-sans'>
              <div>
                <p className='text-12 font-medium text-gray-490'>Before</p>
                <p className='text-20 font-bold text-gray-650'>
                  {retrainBenchmark.before.toFixed(1)}%
                </p>
              </div>
              <div className='text-20 font-bold text-green-600'>→</div>
              <div className='text-right'>
                <p className='text-12 font-medium text-gray-490'>After</p>
                <p className='text-20 font-bold text-green-700'>
                  {retrainBenchmark.after.toFixed(1)}%
                </p>
              </div>
              <div className='rounded-full bg-green-100 px-3 py-1 text-13 font-bold text-green-700'>
                +{retrainBenchmark.delta.toFixed(1)}
              </div>
            </div>
          </div>
        )}
      </div>

      {trainingError && (
        <div className='mb-8 rounded-10 border border-red-200 bg-red-50 p-4'>
          <p className='font-sans text-14 font-medium text-red-600'>{trainingError}</p>
        </div>
      )}

      {voiceClone.hasError && !trainingError && (
        <div className='mb-8 rounded-10 border border-red-200 bg-red-50 p-4'>
          <p className='font-sans text-14 font-medium text-red-600'>
            {voiceClone.uploadError || 'Voice clone upload failed'}
          </p>
        </div>
      )}

      {!trainingError && !voiceClone.hasError && (
        <div className='mb-8'>
          <p className='font-sans text-14 font-medium leading-[22px] text-gray-530'>
            {isFullyDone && idleVideoStatus === 'processing'
              ? 'Your echo is ready. Video generation will continue in the background and can be retried later from Echo Profile.'
              : isFullyDone
                ? "Your echo is ready! Continue to review your echo's personality results and fine-tune settings."
                : "Once complete, you'll be able to review and configure your echo's personality."}
          </p>
        </div>
      )}

      <div className='flex flex-col items-center gap-4 md:flex-row'>
        {isFullyDone && (
          <Button
            variant='purple-dark'
            className='h-[48px] w-full px-8 text-16 font-bold uppercase tracking-wide md:w-auto'
            onClick={() => onTrainingComplete(preloadedConfig ?? undefined)}
          >
            CONTINUE TO RESULTS
          </Button>
        )}

        {voiceClone.hasError && (
          <Button
            variant='purple-dark'
            className='h-[48px] w-full px-8 text-16 font-bold uppercase tracking-wide md:w-auto'
            onClick={onRetryVoiceClone}
          >
            RETRY VOICE CLONE
          </Button>
        )}

        {trainingError && (
          <>
            <Button
              variant='purple-dark'
              className='h-[48px] w-full px-8 text-16 font-bold uppercase tracking-wide md:w-auto'
              onClick={onRetry}
            >
              RETRY
            </Button>
            <button
              onClick={onBackFromProgress}
              className='font-sans text-16 font-semibold text-gray-650 underline decoration-solid transition-colors hover:text-brand-800'
            >
              Go Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
